import * as React from 'react';
import styles from './RoadmapHeader.module.scss';
import {
  Flex,
  ActionButton,
  Text,
  Switch,
  MenuTrigger,
  Menu,
  Item,
  Image
} from '@adobe/react-spectrum';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import View from '@spectrum-icons/workflow/ViewAllTags';
import { useHomeSlice } from 'app/pages/Home/slice';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
  selectParkingLot,
  selectCompact,
  selectUnNestAll
} from 'app/pages/Home/slice/selectors';
import {
  selectTimelineViewMode,
  selectTimelineIconsVisible
} from 'app/components/MonthlyTimeline/store/selectors';
import {
  monthlyTimelineActions,
  useMonthlyTimelineSlice
} from 'app/components/MonthlyTimeline/store/monthlyTimelineSlice';
import { saveUserPreferences, getUserPreferences } from 'utils/helpers';
import { shouldPreferUrlOverLocalPreferences } from 'utils/sharedViewMode';

// Module-level (not component-instance) — restore compact/parking-lot/etc.
// preferences from localStorage once per browser session, not on every
// mount. ViewControls lives inside RoadmapHeader, which remounts on every
// Milestones<->Roadmap transition (separate top-level pages), so a per-mount
// effect re-asserts stale localStorage values right after a Saved View (or
// the shared-preferences restore) has just correctly set isCompact/
// parkingLot, silently clearing the Saved View's active highlight via the
// divergence check in useSavedViewActions.
let hasRestoredViewControlsPreferences = false;

interface ViewOptionProps {
  label: string;
  isSelected?: boolean;
}

function ViewOption({ label, isSelected = false }: ViewOptionProps) {
  return (
    <ActionButton isQuiet aria-label={`${label} view option`}>
      <Flex alignItems="center" gap="size-100">
        <View />
        <Text>{label}</Text>
      </Flex>
    </ActionButton>
  );
}

interface ViewControlsProps {
  hideCompactToggle?: boolean;
  hideIconsToggle?: boolean; // Hide Icons switch (e.g. milestone grid report)
  showBetaToggle?: boolean;
  betaToggleLabel?: string;
  betaToggleSelected?: boolean;
  onBetaToggle?: (value: boolean) => void;
  /** Whether the current route is Roadmap/My Stuff (vs. Milestones) — gates the
   * shared-preference restore below, since that restore is meaningless (and
   * racy against Saved View/My Stuff transitions) while on Milestones. */
  isRoadmapRoute?: boolean;
}

export function ViewControls({
  hideCompactToggle = false,
  hideIconsToggle = false,
  showBetaToggle = false,
  betaToggleLabel = 'Beta',
  betaToggleSelected = false,
  onBetaToggle,
  isRoadmapRoute = true
}: ViewControlsProps) {
  const showParkingLot = useSelector(selectParkingLot);
  const isCompact = useSelector(selectCompact);
  const isUnNested = useSelector(selectUnNestAll);
  const timelineViewMode = useSelector(selectTimelineViewMode);
  const timelineIconsVisible = useSelector(selectTimelineIconsVisible);
  const { actions } = useHomeSlice();
  useMonthlyTimelineSlice();
  const dispatch = useDispatch();

  // Load saved preferences on component mount
  // Skip if deep link is present - deep link takes precedence
  React.useEffect(() => {
    if (hasRestoredViewControlsPreferences) {
      return;
    }

    // This restores shared Roadmap/My Stuff preferences — meaningless (and
    // risky) while mounted on Milestones, since ViewControls is shared
    // between both pages via RoadmapHeader. Don't consume the one-shot flag
    // here so a later Roadmap-route mount still gets to restore.
    if (!isRoadmapRoute) {
      return;
    }
    hasRestoredViewControlsPreferences = true;

    // 🔗 CRITICAL: Check if URL has deep link state
    // If it does, skip localStorage restoration - deep link takes precedence
    if (shouldPreferUrlOverLocalPreferences()) {
      return;
    }

    const preferences = getUserPreferences();

    // Load compact mode preference independently
    if (preferences.isCompact !== undefined) {
      dispatch(actions.setCompact(preferences.isCompact));
    }

    // Load parking lot preference independently (not coupled to compact mode)
    if (preferences.showParkingLot !== undefined) {
      dispatch(actions.setParkingLot(preferences.showParkingLot));
    }

    // Load collapse all (card size) preference
    if (preferences.collapseAll !== undefined) {
      // Set Redux state to match saved preference
      // Note: Redux initial state is true, so we only dispatch if it doesn't match
      const currentState = true; // Redux initial state for collapseAll
      if (preferences.collapseAll !== currentState) {
        dispatch(actions.setCollapseAll()); // This toggles the state
      }
    }

    if (preferences.isUnNested !== undefined) {
      dispatch(actions.setUnNestAll(preferences.isUnNested));
    }

    if (preferences.timelineIconsVisible !== undefined) {
      dispatch(
        monthlyTimelineActions.setTimelineIconsVisible(
          preferences.timelineIconsVisible
        )
      );
    }
  }, [dispatch, actions, isRoadmapRoute]);

  const onParkingLotToggle = () => {
    const newValue = !showParkingLot;
    dispatch(actions.setParkingLot(newValue));
    saveUserPreferences({ showParkingLot: newValue });
  };

  const onTimelineIconsToggle = () => {
    const newValue = !timelineIconsVisible;
    dispatch(monthlyTimelineActions.setTimelineIconsVisible(newValue));
    saveUserPreferences({ timelineIconsVisible: newValue });
  };

  const onCompactToggle = () => {
    const newValue = !isCompact;

    // If disabling compact mode, manually trigger quarter restoration before updating state
    if (!newValue) {
      // This is exiting compact mode - restore expanded timeline sections first
      const preferences = getUserPreferences();
      if (timelineViewMode === 'monthly') {
        if (
          preferences.expandedMonths &&
          Array.isArray(preferences.expandedMonths) &&
          preferences.expandedMonths.length > 0
        ) {
          window.dispatchEvent(
            new CustomEvent('restoreMonths', {
              detail: { expandedMonths: preferences.expandedMonths }
            })
          );
        }
      } else if (
        preferences.expandedQuarters &&
        Array.isArray(preferences.expandedQuarters) &&
        preferences.expandedQuarters.length > 0
      ) {
        window.dispatchEvent(
          new CustomEvent('restoreQuarters', {
            detail: { expandedQuarters: preferences.expandedQuarters }
          })
        );
      }

      // Also restore card size (collapseAll) and unnest state preferences when exiting compact mode
      if (preferences.collapseAll !== undefined) {
        // Dispatch custom event to restore card size after compact mode is disabled
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent('restoreCardSize', {
              detail: { collapseAll: preferences.collapseAll }
            })
          );
        }, 100); // Small delay to ensure compact mode state is updated first
      }

      if (preferences.isUnNested !== undefined) {
        // Dispatch custom event to restore unnest state after compact mode is disabled
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent('restoreUnNestState', {
              detail: { isUnNested: preferences.isUnNested }
            })
          );
        }, 100); // Small delay to ensure compact mode state is updated first
      }
    }

    dispatch(actions.setCompact(newValue));

    // Save compact mode preference without affecting parking lot
    saveUserPreferences({ isCompact: newValue });
  };

  const onUnNestAllToggle = () => {
    const newValue = !isUnNested;
    dispatch(actions.setUnNestAll(newValue));

    // ✅ Clear manually expanded cards from sessionStorage when using global toggle
    sessionStorage.removeItem('manually_expanded_cards');

    // If unnesting all, also ensure collapseAll is false
    if (newValue) {
      dispatch(actions.setCollapseAll());
    }
    saveUserPreferences({ isUnNested: newValue });
  };

  const onCollapseAllClick = () => {
    dispatch(actions.setCollapseAll());
    // Save user preferences when collapsing all cards
    const preferences = getUserPreferences();
    saveUserPreferences({ ...preferences, isUnNested: false });
  };

  return (
    <Flex
      UNSAFE_className={styles['controls-container']}
      data-testid="view-controls-container"
    >
      {/* View by:
      <MenuTrigger>
        <ActionButton aria-label="View by">
          <span style={{ padding: '5px' }}>Quarter</span>
          <ChevronDown />
        </ActionButton>
        <Menu>
          <Item>Quarter</Item>
          <Item>Month</Item>
        </Menu>
      </MenuTrigger> */}
      {!hideIconsToggle &&
        (timelineViewMode === 'monthly' ||
          timelineViewMode === 'quarterly') && (
          <Switch
            data-testid="view-controls-icons-switch"
            isSelected={timelineIconsVisible}
            onChange={onTimelineIconsToggle}
            UNSAFE_className={styles['switch-control']}
          >
            Icons
          </Switch>
        )}
      {showBetaToggle ? (
        <Switch
          data-testid="view-controls-beta-switch"
          isSelected={betaToggleSelected}
          onChange={value => onBetaToggle?.(value)}
          UNSAFE_className={styles['switch-control']}
        >
          {betaToggleLabel}
        </Switch>
      ) : !hideCompactToggle ? (
        <Switch
          data-testid="view-controls-compact-switch"
          isSelected={isCompact}
          onChange={onCompactToggle}
          UNSAFE_className={styles['switch-control']}
        >
          Compact
        </Switch>
      ) : null}
      <Switch
        data-testid="view-controls-parking-lot-switch"
        isSelected={showParkingLot}
        onChange={onParkingLotToggle}
        UNSAFE_className={styles['switch-control']}
      >
        In The Works
      </Switch>
    </Flex>
  );
}
